import type { LanguagesView, LaunchVersionView } from "../api.js";
import { statusLabel } from "../languages/languages.js";
import { exportFilename } from "../render/languageExports.js";

/** The versions that can be exported and shared: ready ones, newest first. */
export function readyVersions(versions: readonly LaunchVersionView[]): LaunchVersionView[] {
  return versions.filter((version) => version.status === "ready").sort((a, b) => b.number - a.number);
}

/**
 * A version as the picker names it: its number, what it was, and the
 * languages it has — so switching to a newer version that has none yet is
 * not a surprise. "v2 · Darker opening · EN, AR (newest)".
 */
export function launchVersionLabel(
  version: Pick<LaunchVersionView, "number" | "request">,
  view: Pick<LanguagesView, "languages"> | null,
  newest: boolean,
): string {
  const made = (view?.languages ?? []).filter((language) => Boolean(language.downloads.video));
  const codes = ["EN", ...made.map((language) => language.code.toUpperCase())].join(", ");
  const what = version.request.length > 32 ? `${version.request.slice(0, 31).trimEnd()}…` : version.request;
  return `v${version.number} · ${what} · ${codes}${newest ? " (newest)" : ""}`;
}

const EXTRAS: { kind: "vtt" | "srt" | "script" | "audio"; label: string }[] = [
  { kind: "vtt", label: ".vtt" },
  { kind: "srt", label: ".srt" },
  { kind: "script", label: "Script" },
  { kind: "audio", label: "Voice" },
];

export interface LaunchRow {
  key: string;
  code: string;
  name: string;
  depth: string;
  chip: { label: string; tone: "ok" | "warn" | "bad" };
  files: { label: string; href: string; download?: string }[];
}

/**
 * The Download tab's rows for one version: the English when its film is
 * there, then each language — Ready with its files once its video is made,
 * else what it is doing (voicing, waiting for a check) or that it failed.
 */
export function launchRows(
  englishDownload: string | null,
  view: Pick<LanguagesView, "languages"> | null,
  projectName: string,
): LaunchRow[] {
  const english: LaunchRow[] = englishDownload
    ? [
        {
          key: "en",
          code: "en",
          name: "English",
          depth: "Original",
          chip: { label: "Ready", tone: "ok" },
          files: [{ label: "MP4", href: englishDownload, download: exportFilename(projectName, null) }],
        },
      ]
    : [];
  const languages = (view?.languages ?? []).map((language): LaunchRow => {
    const video = language.downloads.video;
    return {
      key: language.code,
      code: language.code,
      name: language.nativeName,
      depth: "Voice and subtitles",
      chip: video
        ? { label: "Ready", tone: "ok" }
        : { label: statusLabel(language).text, tone: language.status === "failed" ? "bad" : "warn" },
      files: [
        ...(video ? [{ label: "MP4", href: video, download: exportFilename(projectName, language.code) }] : []),
        ...EXTRAS.flatMap(({ kind, label }) => {
          const href = language.downloads[kind];
          return href ? [{ label, href }] : [];
        }),
      ],
    };
  });
  return [...english, ...languages];
}
